import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  EDIT_ALLOWLIST,
  CONTENT_API_BASE,
  SESSION_LOCAL_KEY,
} from '../siteConfig';

// Auth for edit mode. Email magic-code is the primary method (Signal is off for
// now). In LOCAL mode (no CONTENT_API_BASE) there is no delivery or server:
// sign-in just checks the allowlist and issues a browser-local session so the
// edit UX can be exercised before any backend exists.
//
// Anti-phishing: a code request returns four emoji that also appear in the
// email — the editor confirms they match before typing the code.
//
// Passkeys: after a successful email sign-in the editor may register a passkey;
// subsequent sign-ins can use it (WebAuthn) with no code.

export type AuthMethod = 'email' | 'signal';

export interface Session {
  editor: string;
  method: AuthMethod | 'passkey';
  token: string;
  local: boolean;
}

interface AuthCtxValue {
  session: Session | null;
  isLocal: boolean;
  editing: boolean;
  setEditing: (on: boolean) => void;
  requestCode: (method: AuthMethod, identifier: string) => Promise<RequestResult>;
  verifyCode: (method: AuthMethod, identifier: string, code: string) => Promise<VerifyResult>;
  passkeySupported: boolean;
  registerPasskey: () => Promise<{ ok: boolean; error?: string }>;
  signInWithPasskey: () => Promise<VerifyResult>;
  signOut: () => void;
}

interface RequestResult { ok: boolean; hint?: string; error?: string; emojis?: string[] }
interface VerifyResult { ok: boolean; error?: string; editor?: string }

const AuthCtx = createContext<AuthCtxValue | null>(null);

const IS_LOCAL = !CONTENT_API_BASE;

// A small stable emoji set for the local-preview match code (the server picks
// its own set in production).
const LOCAL_EMOJI = ['🎻', '🎼', '🌙', '⭐', '🎹', '🪕', '🎺', '🔔', '🍃', '🕊️'];

function normalize(method: AuthMethod, id: string): string {
  return method === 'email' ? id.trim().toLowerCase() : id.trim();
}

function phoneDigits(s: string): string {
  const d = (s.match(/\d/g) || []).join('');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

function isAllowed(method: AuthMethod, id: string): boolean {
  if (method === 'email') {
    const norm = id.trim().toLowerCase();
    return EDIT_ALLOWLIST.emails.map(e => e.toLowerCase()).includes(norm);
  }
  const norm = phoneDigits(id);
  return norm.length >= 10 && EDIT_ALLOWLIST.signalNumbers.map(phoneDigits).includes(norm);
}

// ── base64url <-> ArrayBuffer helpers for WebAuthn ───────────
function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = (s.replace(/-/g, '+').replace(/_/g, '/')) + pad;
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function apiPost(path: string, body: unknown, token?: string): Promise<Response> {
  return fetch(`${CONTENT_API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(SESSION_LOCAL_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  });
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (session) localStorage.setItem(SESSION_LOCAL_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_LOCAL_KEY);
  }, [session]);

  const passkeySupported = typeof window !== 'undefined' &&
    !!(window.PublicKeyCredential) && !IS_LOCAL;

  const requestCode = useCallback(async (method: AuthMethod, identifier: string): Promise<RequestResult> => {
    const id = normalize(method, identifier);
    if (!id) return { ok: false, error: 'Enter your ' + (method === 'email' ? 'email' : 'Signal number') + '.' };
    if (!isAllowed(method, id)) {
      return { ok: false, error: "That address isn't whitelisted as an editor." };
    }
    if (IS_LOCAL) {
      const seed = id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
      const emojis = [0, 1, 2, 3].map(i => LOCAL_EMOJI[(seed + i * 7) % LOCAL_EMOJI.length]);
      return { ok: true, hint: 'Local preview — enter any code to continue.', emojis };
    }
    const res = await apiPost(`/auth/${method}/request`, { identifier: id });
    if (!res.ok) return { ok: false, error: 'Could not send a code. Try again.' };
    const data = await res.json().catch(() => ({}));
    return {
      ok: true,
      hint: method === 'email' ? 'Check your email for a code.' : 'Check Signal for a code.',
      emojis: Array.isArray(data.emojis) ? data.emojis : undefined,
    };
  }, []);

  const verifyCode = useCallback(async (method: AuthMethod, identifier: string, code: string): Promise<VerifyResult> => {
    const id = normalize(method, identifier);
    if (!isAllowed(method, id)) return { ok: false, error: "That address isn't whitelisted as an editor." };

    if (IS_LOCAL) {
      if (!code.trim()) return { ok: false, error: 'Enter any code to continue.' };
      const token = 'local-' + (globalThis.crypto?.randomUUID?.() ?? String(Date.now()));
      setSession({ editor: id, method, token, local: true });
      setEditing(true);
      return { ok: true, editor: id };
    }

    const res = await apiPost(`/auth/${method}/verify`, { identifier: id, code: code.trim() });
    if (!res.ok) return { ok: false, error: 'That code didn’t work.' };
    const { token } = (await res.json()) as { token: string };
    setSession({ editor: id, method, token, local: false });
    setEditing(true);
    return { ok: true, editor: id };
  }, []);

  // ── passkey registration (requires an active server session) ──
  const registerPasskey = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!session || session.local) return { ok: false, error: 'Sign in first.' };
    try {
      const optRes = await apiPost('/auth/passkey/register/options', {}, session.token);
      if (!optRes.ok) return { ok: false, error: 'Could not start passkey setup.' };
      const opt = await optRes.json();
      const cred = await navigator.credentials.create({
        publicKey: {
          challenge: b64urlToBuf(opt.challenge),
          rp: opt.rp,
          user: {
            id: b64urlToBuf(opt.user.id),
            name: opt.user.name,
            displayName: opt.user.displayName,
          },
          pubKeyCredParams: opt.pubKeyCredParams,
          authenticatorSelection: opt.authenticatorSelection,
          timeout: 60000,
          attestation: 'none',
        },
      }) as PublicKeyCredential | null;
      if (!cred) return { ok: false, error: 'Passkey setup was cancelled.' };
      const resp = cred.response as AuthenticatorAttestationResponse;
      // Modern browsers export the public key directly — no server-side CBOR.
      const spki = typeof resp.getPublicKey === 'function' ? resp.getPublicKey() : null;
      const alg = typeof resp.getPublicKeyAlgorithm === 'function' ? resp.getPublicKeyAlgorithm() : -7;
      if (!spki) return { ok: false, error: 'This device could not export a passkey.' };
      const verifyRes = await apiPost('/auth/passkey/register/verify', {
        credentialId: cred.id,
        publicKey: bufToB64url(spki),
        alg,
      }, session.token);
      if (!verifyRes.ok) return { ok: false, error: 'Could not save the passkey.' };
      return { ok: true };
    } catch (err) {
      return { ok: false, error: 'Passkey setup failed on this device.' };
    }
  }, [session]);

  const signInWithPasskey = useCallback(async (): Promise<VerifyResult> => {
    if (IS_LOCAL) return { ok: false, error: 'Passkeys need the live site.' };
    try {
      const optRes = await apiPost('/auth/passkey/login/options', {});
      if (!optRes.ok) return { ok: false, error: 'No passkeys are registered yet.' };
      const opt = await optRes.json();
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: b64urlToBuf(opt.challenge),
          rpId: opt.rpId,
          allowCredentials: (opt.allowCredentials || []).map((c: { id: string }) => ({
            id: b64urlToBuf(c.id), type: 'public-key' as const,
          })),
          userVerification: 'preferred',
          timeout: 60000,
        },
      }) as PublicKeyCredential | null;
      if (!assertion) return { ok: false, error: 'Passkey sign-in was cancelled.' };
      const r = assertion.response as AuthenticatorAssertionResponse;
      const verifyRes = await apiPost('/auth/passkey/login/verify', {
        credentialId: assertion.id,
        authenticatorData: bufToB64url(r.authenticatorData),
        clientDataJSON: bufToB64url(r.clientDataJSON),
        signature: bufToB64url(r.signature),
      });
      if (!verifyRes.ok) return { ok: false, error: 'Passkey not recognized.' };
      const { token, editor } = (await verifyRes.json()) as { token: string; editor: string };
      setSession({ editor, method: 'passkey', token, local: false });
      setEditing(true);
      return { ok: true, editor };
    } catch (err) {
      return { ok: false, error: 'Passkey sign-in failed.' };
    }
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
    setEditing(false);
  }, []);

  const value: AuthCtxValue = useMemo(
    () => ({
      session,
      isLocal: IS_LOCAL,
      editing: editing && !!session,
      setEditing,
      requestCode,
      verifyCode,
      passkeySupported,
      registerPasskey,
      signInWithPasskey,
      signOut,
    }),
    [session, editing, requestCode, verifyCode, passkeySupported, registerPasskey, signInWithPasskey, signOut]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthCtxValue {
  const c = useContext(AuthCtx);
  if (!c) throw new Error('useAuth must be used within <AuthProvider>');
  return c;
}
